import { dispatchCanonicalEventAfterCommit } from "../../../../runtime/events/hooks.ts";
import { deriveIngressProviderEventId } from "../../../../runtime/ingress/identity.ts";
import type { AthenaIngressIR } from "../../../../runtime/ingress/ir.ts";
import type { EventIngressDomainHandler } from "../../../../runtime/ingress/runtime.ts";
import {
	type CanonicalBillingDocument,
	canonicalDocumentRevision,
	canonicalDocumentSubjectId,
} from "../../../canonical/document.ts";
import type { CanonicalBillingEvent } from "../../../canonical/transition.ts";
import {
	type BillingWebhookIngressObservability,
	recordBillingWebhookIngressQuietly,
	reportBillingWebhookIngressDiagnostic,
} from "../../../ingestion/observability/health.ts";
import {
	type BillingWebhookIngressStage,
	billingWebhookRegistrationKind,
} from "../../../ingestion/observability/stages.ts";
import type { BillingProviderWebhookPort } from "../providers/webhook-port.ts";
import { applyCanonicalBillingIngress } from "./apply.ts";
import type { BillingIngressDomainContext } from "./repository.ts";

export function billingDeterministicEventId(input: {
	provider: string;
	connectionId?: string;
	document: CanonicalBillingDocument;
}): string {
	const revision = canonicalDocumentRevision(input.document);
	return deriveIngressProviderEventId({
		provider: input.provider,
		connectionId: input.connectionId,
		resourceKind: input.document.kind,
		resourceId: canonicalDocumentSubjectId(input.document),
		authoritativeStateOrVersion:
			revision.sequence ??
			`${input.document.status}:${revision.providerUpdatedAt?.toISOString() ?? ""}`,
	});
}

function observeContext(ingress: AthenaIngressIR) {
	return {
		kind: billingWebhookRegistrationKind(ingress.operation),
		...(ingress.connectionId ? { connectionId: ingress.connectionId } : {}),
		...(ingress.correlationId ? { correlationId: ingress.correlationId } : {}),
		ingressId: ingress.id,
		...(ingress.traceId ? { traceId: ingress.traceId } : {}),
	};
}

function observabilityWrite(
	ctx: ReturnType<typeof observeContext>,
	connectionId: string,
	stage: BillingWebhookIngressStage,
) {
	return {
		connectionId,
		kind: ctx.kind,
		stage,
		ingressId: ctx.ingressId,
		...(ctx.correlationId ? { correlationId: ctx.correlationId } : {}),
		...(ctx.traceId ? { traceId: ctx.traceId } : {}),
	};
}

export function createBillingEventIngressHandler<TEnvelope, TResource>(input: {
	observability?: BillingWebhookIngressObservability;
	onAuthoritativeDocument?: (input: {
		document: CanonicalBillingDocument;
		ingress: AthenaIngressIR;
	}) => Promise<void> | void;
	port: BillingProviderWebhookPort<TEnvelope, TResource>;
	provider?: string;
}): EventIngressDomainHandler<
	BillingIngressDomainContext,
	CanonicalBillingDocument,
	CanonicalBillingEvent
> {
	const provider = input.provider ?? "mollie";
	return {
		async ingest(ingress, runtime) {
			const ctx = observeContext(ingress);
			const connectionId = ctx.connectionId;
			const note = async (stage: BillingWebhookIngressStage) => {
				await recordBillingWebhookIngressQuietly(input.observability, (store) =>
					store.recordStage({
						...ctx,
						occurredAt: new Date(),
						stage,
					}),
				);
			};
			const acceptDuplicate = async () => {
				if (connectionId) {
					await recordBillingWebhookIngressQuietly(
						input.observability,
						(store) =>
							store.recordAccepted(
								observabilityWrite(ctx, connectionId, "duplicate"),
							),
					);
				} else {
					await note("duplicate");
				}
				return {
					duplicate: true,
					events: [],
					reconciliation: "skipped" as const,
				};
			};
			const early = await runtime.database.transaction(async (tx) => {
				await runtime.persistence.insertReceived(tx, ingress, {
					provider,
				});
				const existing = await runtime.persistence.lock(tx, ingress.id);
				if (existing?.status === "processed") {
					return { duplicate: true as const };
				}
				await runtime.persistence.markStatus(tx, {
					ingressId: ingress.id,
					status: "resolving",
				});
				return { duplicate: false as const };
			});
			if (early.duplicate) {
				return acceptDuplicate();
			}
			const envelope = await input.port.parseIngress(ingress);
			await note("parsed");
			if (input.port.verification !== "authoritative_refetch") {
				await note("verified");
			}
			await note("authoritative_refetch_started");
			const resource = await input.port.resolveAuthoritativeState(
				envelope,
				ingress,
			);
			await note("authoritative_refetch_completed");
			const document = input.port.projectDocument(resource);
			await note("canonicalized");
			const providerEventId = billingDeterministicEventId({
				provider,
				connectionId: ingress.connectionId,
				document,
			});
			const claimed = await runtime.database.transaction((tx) =>
				runtime.persistence.insertReceived(tx, ingress, {
					provider,
					providerEventId,
				}),
			);
			if (claimed.duplicate) {
				return acceptDuplicate();
			}
			const result = await applyCanonicalBillingIngress({
				database: runtime.database,
				ingress,
				document,
				persistence: runtime.persistence,
				documents: runtime.domain.documents,
				events: runtime.domain.events,
				outbox: runtime.domain.outbox,
				canonicalizeEvents: (context) => input.port.canonicalizeEvents(context),
			});
			const acceptStage: BillingWebhookIngressStage = result.duplicate
				? "duplicate"
				: "persisted";
			if (connectionId) {
				await recordBillingWebhookIngressQuietly(input.observability, (store) =>
					store.recordAccepted(
						observabilityWrite(ctx, connectionId, acceptStage),
					),
				);
			} else {
				await note(acceptStage);
			}
			let reconciliation: "completed" | "failed" | "skipped" = "skipped";
			if (!result.duplicate) {
				try {
					await dispatchCanonicalEventAfterCommit(result.events);
					if (input.onAuthoritativeDocument) {
						await note("reconciliation_started");
						await input.onAuthoritativeDocument({ document, ingress });
						if (connectionId) {
							await recordBillingWebhookIngressQuietly(
								input.observability,
								(store) =>
									store.recordReconciliation({
										...observabilityWrite(
											ctx,
											connectionId,
											"reconciliation_completed",
										),
										outcome: "completed",
									}),
							);
						} else {
							await note("reconciliation_completed");
						}
						reconciliation = "completed";
					} else if (connectionId) {
						await recordBillingWebhookIngressQuietly(
							input.observability,
							(store) =>
								store.recordReconciliation({
									...observabilityWrite(ctx, connectionId, acceptStage),
									outcome: "skipped",
								}),
						);
					}
				} catch (error) {
					reportBillingWebhookIngressDiagnostic(
						"[athena.billing] webhook reconciliation failed after accept",
						error,
					);
					if (connectionId) {
						await recordBillingWebhookIngressQuietly(
							input.observability,
							(store) =>
								store.recordReconciliation({
									...observabilityWrite(
										ctx,
										connectionId,
										"reconciliation_failed",
									),
									outcome: "failed",
								}),
						);
					} else {
						await note("reconciliation_failed");
					}
					reconciliation = "failed";
				}
			}
			return {
				...result,
				reconciliation,
			};
		},
	};
}
